import { FormSection, FormField, CheckboxGroupField } from '@/types/formSchema'

interface DynamicFormRendererProps {
  sections: FormSection[]
  formData: Record<string, any>
  onChange: (fieldId: string, value: any) => void
  errors?: Record<string, string>
}

export default function DynamicFormRenderer({ sections, formData, onChange, errors }: DynamicFormRendererProps) {
  // Get width class based on field width
  const getWidthClass = (field: FormField | CheckboxGroupField) => {
    switch (field.width) {
      case 'half': return 'w-full md:w-1/2'
      case 'third': return 'w-full md:w-1/3'
      case 'quarter': return 'w-full md:w-1/4'
      default: return 'w-full'
    }
  }

  const renderField = (field: FormField | CheckboxGroupField, sectionId: string) => {
    if (!field.enabled) return null

    const fieldKey = `${sectionId}_${field.id}`
    const value = formData[fieldKey] ?? field.defaultValue ?? ''
    const hasError = errors?.[fieldKey]

    const baseClasses = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
      hasError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
    }`

    switch (field.type) {
      case 'text':
        return (
          <div key={fieldKey} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              placeholder={field.placeholder}
              className={baseClasses}
            />
            {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
          </div>
        )

      case 'textarea':
        return (
          <div key={fieldKey} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              placeholder={field.placeholder}
              rows={4}
              className={baseClasses}
            />
            {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
          </div>
        )

      case 'number':
        return (
          <div key={fieldKey} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              placeholder={field.placeholder}
              className={baseClasses}
            />
            {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
          </div>
        )

      case 'select':
        return (
          <div key={fieldKey} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              className={baseClasses}
            >
              <option value="">-- Chọn --</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
          </div>
        )

      case 'checkbox':
        return (
          <div key={fieldKey} className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => onChange(fieldKey, e.target.checked)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">{field.label}</span>
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
          </div>
        )

      case 'checkboxGroup':
        const checkboxGroupField = field as CheckboxGroupField
        return (
          <div key={fieldKey} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {checkboxGroupField.options?.filter(opt => opt.enabled).map((option) => {
                const optionKey = `${fieldKey}_${option.id}`
                const optionValue = formData[optionKey] ?? option.defaultValue ?? false
                return (
                  <label key={optionKey} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={!!optionValue}
                      onChange={(e) => onChange(optionKey, e.target.checked)}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                )
              })}
            </div>
            {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
          </div>
        )

      case 'radio':
        return (
          <div key={fieldKey} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {field.options?.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name={fieldKey}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => onChange(fieldKey, e.target.value)}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
            {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
          </div>
        )

      case 'date':
        return (
          <div key={fieldKey} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={value}
              onChange={(e) => onChange(fieldKey, e.target.value)}
              className={baseClasses}
            />
            {hasError && <p className="text-red-500 text-xs mt-1">{hasError}</p>}
          </div>
        )

      default:
        return null
    }
  }

  const renderSection = (section: FormSection) => {
    if (!section.enabled) return null

    const fields = section.fields
      .filter((field) => field.enabled)
      .sort((a, b) => a.order - b.order)

    return (
      <div key={section.id} className="mb-6">
        <div
          className="text-lg font-semibold mb-4 pb-2 border-b-2"
          style={{ borderColor: section.color || '#3b82f6' }}
        >
          {section.title}
        </div>
        <div className="flex flex-wrap gap-4">
          {fields.map((field) => {
            const widthClass = getWidthClass(field)
            const newRowClass = field.newRow ? 'w-full' : ''

            return (
              <div
                key={`${section.id}_${field.id}`}
                className={`${newRowClass || widthClass} ${field.newRow ? 'clear-both' : ''}`}
              >
                {renderField(field, section.id)}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {sections
        .filter((section) => section.enabled)
        .sort((a, b) => a.order - b.order)
        .map((section) => renderSection(section))}
    </div>
  )
}
